import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Card, CardBody, Col, Row } from "reactstrap";
import { getClientKpi } from "src/store/clients/saga";
import { ClientKpiDto } from "src/store/clients/type";

/**
 * Müşteri kullanım göstergeleri.
 *
 * "Son giriş" alanı yeni eklendi ve GERİYE DÖNÜK VERİ YOK — dağıtımdan önce
 * girmiş kullanıcılar boş görünür. Bunu kullanıcıya yazıyoruz, yoksa "veri
 * bozuk" diye algılanıyor.
 */
const ClientKpiCards = ({ clientId }: { clientId: string }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [kpi, setKpi] = useState<ClientKpiDto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // clientId henüz gelmediyse sonsuza kadar spinner gösterme.
    if (!clientId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    dispatch(
      getClientKpi({
        payload: {
          onSuccess: (_m: string, p: ClientKpiDto) => {
            setKpi(p);
            setLoading(false);
          },
          onError: () => setLoading(false),
        },
        id: clientId,
      })
    );
  }, [dispatch, clientId]);

  const date = (v?: string | null) =>
    v ? new Date(v).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" }) : "—";

  const daysSince = (v?: string | null) => {
    if (!v) return null;
    const d = Math.floor((Date.now() - new Date(v).getTime()) / 86400000);
    return d;
  };

  // Sessizlik uyarısı: 30 günden uzun süredir giriş yoksa dikkat çek.
  const idle = daysSince(kpi?.LastLoginTime);
  const idleTone = idle === null ? "" : idle > 30 ? "text-danger" : idle > 7 ? "text-warning" : "text-success";

  const tiles = [
    {
      i: "mdi-login",
      label: t("Clients.Kpi.LastLogin"),
      value: kpi?.LastLoginTime ? date(kpi.LastLoginTime) : t("Clients.Kpi.NoLoginYet"),
      tone: idleTone,
      hint: idle !== null ? t("Clients.Kpi.DaysAgo", { count: idle }) : t("Clients.Kpi.NoHistory"),
    },
    { i: "mdi-car-multiple", label: t("Clients.Kpi.Assets"), value: kpi?.AssetCount ?? 0 },
    { i: "mdi-card-bulleted-outline", label: t("Clients.Kpi.AssetCards"), value: kpi?.AssetCardCount ?? 0 },
    { i: "mdi-calendar-check", label: t("Clients.Kpi.Reservations"), value: kpi?.ReservationCount ?? 0 },
    {
      i: "mdi-file-document-outline",
      label: t("Clients.Kpi.Contracts"),
      value: kpi?.ContractCount ?? 0,
      hint: t("Clients.Kpi.Last30", { count: kpi?.ContractCountLast30Days ?? 0 }),
    },
    { i: "mdi-office-building-outline", label: t("Clients.Kpi.Offices"), value: kpi?.OfficeCount ?? 0 },
    { i: "mdi-account-multiple-outline", label: t("Clients.Kpi.Users"), value: kpi?.UserCount ?? 0 },
    { i: "mdi-clock-start", label: t("Clients.Kpi.FirstContract"), value: date(kpi?.FirstContractDate) },
  ];

  if (loading) {
    return (
      <Card>
        <CardBody className="text-center text-muted py-4">
          <i className="mdi mdi-loading mdi-spin me-2" />
          {t("Components.Loading")}
        </CardBody>
      </Card>
    );
  }

  return (
    <Row className="g-2">
      {tiles.map((tile, i) => (
        <Col key={i} xl={3} lg={4} md={6} xs={12}>
          <Card className="mb-2 h-100">
            <CardBody className="d-flex align-items-start gap-3">
              <span className="avatar-sm rounded bg-light d-flex align-items-center justify-content-center flex-shrink-0">
                <i className={"mdi font-size-20 text-primary " + tile.i} />
              </span>
              <div className="min-w-0">
                <div className="text-muted font-size-12">{tile.label}</div>
                <div className={"fw-semibold text-truncate " + (tile.tone || "")}>{tile.value}</div>
                {tile.hint ? <div className="text-muted font-size-11">{tile.hint}</div> : null}
              </div>
            </CardBody>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

export default ClientKpiCards;
